import React from 'react';
import { Divider, Menu, MenuItem } from '@mui/material';
import clsx from 'clsx';

import './BaseFilter.scss';

import CheckTick from '@/components/Icons/icons/CheckTick';
import OutlinedFilterIcon from '@/components/Icons/icons/OutlinedFilterIcon';

interface FilterOption {
  IconComponent?: React.ComponentType<any>;
  label: string;
  key: string;
  value?: string;
}

interface BaseFilterProps {
  triggerComponent?: React.ComponentType<{ isOpen: boolean }>;
  icon?: React.ComponentType<any>;
  filterTitle?: string;
  options: FilterOption[];
  handleInsertFilter: (option: FilterOption) => void;
  appliedFiltersKey: string[];
  selectedOption?: string[];
}

const BaseFilter: React.FC<BaseFilterProps> = ({
  triggerComponent: TriggerComponent,
  icon,
  filterTitle,
  options,
  handleInsertFilter,
  selectedOption = [],
  appliedFiltersKey = [],
}): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (option: FilterOption) => {
    handleInsertFilter(option);
    handleClose();
  };

  const FilterIconComponent = icon || <OutlinedFilterIcon width={20} height={20} color="var(--icon-default)" />;

  const defaultTrigger = (
    <div
      onClick={handleClick}
      className={clsx('filter-trigger', { active: appliedFiltersKey.length > 0 || selectedOption.length > 0 })}>
      {FilterIconComponent}
      {appliedFiltersKey.length > 0 && <div className="filter-trigger__badge">{appliedFiltersKey.length}</div>}
    </div>
  );

  const filteredOptions = options.filter((option) => !appliedFiltersKey.includes(option.key));

  if (filteredOptions.length === 0 && TriggerComponent !== undefined) return <></>;

  return (
    <>
      {TriggerComponent ? (
        <div onClick={handleClick}>
          <TriggerComponent isOpen={open} />
        </div>
      ) : (
        defaultTrigger
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <div className="filter-menu__container">
          {filterTitle && (
            <>
              <div className="filter-menu__title">{filterTitle}</div>
              <Divider />
            </>
          )}
          <div className="filter-menu__items">
            {filteredOptions.map((option) => {
              const isSelected = selectedOption.includes(option.value || option.key);
              return (
                <MenuItem
                  key={option.key}
                  onClick={() => handleOptionClick(option)}
                  className={clsx('filter-menu__item', { selected: isSelected })}>
                  <div className="flex items-center gap-2 flex-1">
                    {option.IconComponent ? (
                      <option.IconComponent
                        color={isSelected ? 'var(--purple-p-400)' : 'var(--icon-default)'}
                        height={20}
                        width={20}
                      />
                    ) : null}
                    <span className={clsx({ 'text-highlight': isSelected })}>{option.label}</span>
                  </div>
                  {isSelected && <CheckTick color="var(--purple-p-400)" />}
                </MenuItem>
              );
            })}
          </div>
        </div>
      </Menu>
    </>
  );
};

export default BaseFilter;
